import queryString from 'query-string';

import ContentType from '@/helpers/contentType';
import { createAxiosInstance } from '@/plugins/axios.plugin';
import { validateQueryParameters } from '@/helpers/verifyQueryParameters';

export class ContentServiceV2 {
  static SERVICE_NAME = 'contentV2';

  constructor() {
    this.client = createAxiosInstance({
      baseURL        : process.env.PLATAFORMA_API_V2,
      useAccessToken : true
    });
  }

  getCourseByAcademicMainId(academicMainId) {
    const headers = {};

    const savedSignature = localStorage.getItem("plataforma_noticeToken");
    if (savedSignature) {
      try {
        const signatureData = JSON.parse(savedSignature);
        if (signatureData) {
          headers["x-notice-show-modal"] = signatureData.shouldShowModal;
          headers["x-notice-expired-at"] = signatureData.expiredAt;
          headers["x-notice-signature"] = signatureData.signature;
        } else {
          localStorage.removeItem("plataforma_noticeToken");
        }
      } catch (e) {
        localStorage.removeItem("plataforma_noticeToken");
      }
    }

    return this.client.get(`/content/academics-main/${academicMainId}/contents`, {
      passOnMobileHeaders: true,
      headers
    });
  }

  getContentByTopicId(params) {
    const stringified = queryString.stringify(
      { isForGetPosts: params.isForGetPosts, mainGroupId: params.mainGroupId },
      { skipNull: true, skipEmptyString: true }
    );
    const headers = {};
    const savedSignature = localStorage.getItem("plataforma_noticeToken");

    if (savedSignature) {
      try {
        const signatureData = JSON.parse(savedSignature);
        if (signatureData) {
          headers["x-notice-show-modal"] = signatureData.shouldShowModal;
          headers["x-notice-expired-at"] = signatureData.expiredAt;
          headers["x-notice-signature"] = signatureData.signature;
        } else {
          localStorage.removeItem("plataforma_noticeToken");
        }
      } catch (e) {
        localStorage.removeItem("plataforma_noticeToken");
      }
    }
    return this.client.get(
      `/content/academics-main/${params.academicMainId}/topics/${params.topicId}${stringified && `?${stringified}`}`,
      { passOnMobileHeaders: true, headers }
    );
  }

  postCreateTopic(params) {
    return this.client.post(`/content/academics-main/${params.academicMainId}/topics`, params.topic);
  }

  postGroupTopic(params) {
    return this.client.post(`/content/academics-main/${params.academicMainId}/topics/${params.topicId}/grouping`);
  }

  patchReorderTopic(params) {
    return this.client.patch(`/content/academics-main/${params.academicMainId}/topics/${params.topic.id}/sequences`, {
      version       : params.version,
      newSequence   : params.newSequence + 1,
      parentTopicId : params.parentTopicId
    });
  }

  patchMoveToTopic(params) {
    return this.client.patch(`/content/academics-main/${params.academicMainId}/topics/${params.topic.id}/sequences`, {
      version       : params.version,
      newSequence   : null,
      parentTopicId : params.newParentTopicId
    });
  }

  patchTitleTopic(params) {
    return this.client.patch(`/content/academics-main/${params.academicMainId}/topics/${params.topicId}`, {
      title   : params.title,
      version : params.version
    });
  }

  updateTopicBanner(params) {
    return this.client.put(`/content/academics-main/${params.academicMainId}/topics/${params.topicId}/banner`, {
      bannerDesignId           : params.bannerDesignId,
      bannerValue              : params.bannerValue,
      bannerTitle              : params.bannerTitle,
      bannerTitleDesignId      : params.bannerTitleDesignId,
      bannerTitleAlignDesignId : params.bannerTitleAlignDesignId,
      bannerDetailsColor       : params.bannerDetailsColor,
      version                  : params.version
    });
  }

  patchIsVisibleTopic(params) {
    return this.client.patch(`/content/academics-main/${params.academicMainId}/topics/${params.topic.id}`, {
      isVisible : !params.topic.isVisible,
      version   : params.version
    });
  }

  deleteTopic(params) {
    return this.client.delete(`/content/academics-main/${params.academicMainId}/topics/${params.topic.id}`, {
      params: { version: params.version }
    });
  }

  putUpdateTopic(params) {
    const content = {
      id                                 : params.data.id,
      title                              : params.data.title,
      content                            : params.data.content,
      version                            : params.data.version,
      isRecordProgress                   : params.data.isRecordProgress,
      progressTypeId                     : params.data.progressTypeId,
      hasEditedContent                   : params.data.hasEditedContent,
      hasRubric                          : params.data.hasRubric,
      rubricId                           : params.data.rubricId,
      isRubricVisibleForStudent          : params.data.isRubricVisibleForStudent,
      hasDeadline                        : params.data.hasDeadline,
      deadlineAt                         : params.data.deadlineAt,
      hasGroup                           : params.data.hasGroup,
      groupSetId                         : params.data.groupSetId,
      categoryTypeId                     : params.data.categoryTypeId,
      topicTypeId                        : params.data.topicTypeId,
      parentTopicId                      : params.data.parentTopicId,
      parentTopicTypeId                  : params.data.parentTopicTypeId,
      isVisible                          : params.data.isVisible,
      sequence                           : params.data.sequence,
      enrollmentIdsWhoLiked              : params.data.enrollmentIdsWhoLiked,
      gradeBookDetail                    : params.data.gradeBookDetail,
      hasGrade                           : params.data.hasGrade,
      hasNavigationButtons               : params.data.hasNavigationButtons,
      grade                              : params.data.grade,
      hasSimilarity                      : params.data.hasSimilarity,
      similarity                         : params.data.similarity,
      hasAutoGrade                       : params.data.hasAutoGrade,
      autoGrade                          : params.data.autoGrade,
      design                             : params.data.design,
      hasConditional                     : params.data.hasConditional,
      conditionalSetId                   : params.data.conditionalSetId,
      conditionalSetOperator             : params.data.conditionalSetOperator || 'AND',
      conditionals                       : params.data.conditionals,
      isVisibleIsControlledByConditional : params.data.isVisibleIsControlledByConditional,
      wasAnswered                        : params.data.wasAnswered,
      hasContentEvaluation               : params.data.hasContentEvaluation,
      moduleDefaultGradeBookId           : params.data.moduleDefaultGradeBookId,
      ...(params.data.topicTypeId === ContentType.AVALIA && {
        isAccessCodeEnabled : params.data.isAccessCodeEnabled,
        accessCode          : params.data.accessCode,
        startAt             : params.data.startAt
      })
    };

    const stringified = queryString.stringify(
      {
        isToForceRemoveChildrenDeadline : params.data.isToForceRemoveChildrenDeadline,
        hasToUpdateChildrenProgress     : params.data.hasToUpdateChildrenProgress
      },
      { skipNull: true, skipEmptyString: true }
    );

    return this.client.put(
      `/content/academics-main/${params.academicMainId}/topics/${params.data.id}${stringified && `?${stringified}`}`,
      content
    );
  }

  patchUpdateTopic(params) {
    const content = {
      hasConditional                     : params.data.hasConditional,
      conditionalSetId                   : params.data.conditionalSetId,
      conditionalSetOperator             : params.data.conditionalSetOperator || 'AND',
      conditionals                       : params.data.conditionals,
      isVisibleIsControlledByConditional : params.data.isVisibleIsControlledByConditional,
      version                            : params.data.version
    };

    return this.client.patch(`/content/academics-main/${params.academicMainId}/topics/${params.data.id}`, content);
  }

  getConfigurationsByTopicId(params) {
    return this.client.get(`/content/academics-main/${params.academicMainId}/topics/${params.topicId}/configurations`);
  }

  markProgress(params) {
    return this.client.post(`/content/academics-main/${params.academicMainId}/topics/${params.topicId}/progress`);
  }

  markScormProgress({ academicMainId, topicId }) {
    return this.client.post(`/content/academic-main/${academicMainId}/topics/${topicId}/scorm/progress`);
  }

  removeProgress(params) {
    return this.client.delete(`/content/academics-main/${params.academicMainId}/topics/${params.topicId}/progress`);
  }

  answerQuizQuestion(params) {
    return this.client.post(`content/academics-main/${params.academicMainId}/topics/${params.topicId}/quizzes`, {
      questionId : params.questionId,
      optionId   : params.optionId
    });
  }

  finishQuizAttempt(params) {
    return this.client.post(
      `content/academics-main/${params.academicMainId}/topics/${params.topicId}/quizzes/attempts`,
      {
        attemptId: params.attemptId
      }
    );
  }

  reintegrateWebconference(params) {
    const content = {
      id             : params.data.id,
      title          : params.data.title,
      categoryTypeId : params.data.categoryTypeId,
      topicTypeId    : params.data.topicTypeId,
      version        : params.data.version,
      design         : params.data.design,
      sequence       : params.data.sequence,
      content        : {
        description                : params.data.content.description,
        startAt                    : params.data.content.startAt,
        endAt                      : params.data.content.endAt,
        isDownloadRecordingEnabled : params.data.content.isDownloadRecordingEnabled,
        isLargeSessionEnabled      : params.data.content.isLargeSessionEnabled,
        isAllowGuestsEnabled       : params.data.content.isAllowGuestsEnabled,
        isEarlyAccessEnabled       : params.data.content.isEarlyAccessEnabled,
        earlyAccessTime            : params.data.content.earlyAccessTime,
        guestURL                   : params.data.content.guestURL,
        link                       : params.data.content.link,
        contextId                  : params.data.content.contextId,
        isIntegrated               : params.data.content.isIntegrated,
        provider                   : params.data.content.provider
      }
    };

    return this.client.put(
      `/content/academics-main/${params.academicMainId}/topics/${params.topicId}/webconference/reintegrate`,
      content
    );
  }

  getWebconferenceProviders(params) {
    return this.client.get(`/content/academics-main/${params.academicMainId}/webconference/providers`);
  }

  getWebconferenceProviderList() {
    return this.client.get(`/content/webconference/providers`);
  }

  getWebconferenceURL(params) {
    const stringified = queryString.stringify(
      { name: params.name, email: params.email },
      { skipNull: true, skipEmptyString: true }
    );
    return this.client.get(
      `/content/academics-main/${params.academicMainId}/topics/${params.topicId}/webconference/url${stringified && `?${stringified}`
      }`
    );
  }

  getWebconferenceRecords(params) {
    return this.client.get(
      `/content/academics-main/${params.academicMainId}/topics/${params.topicId}/webconference/records`
    );
  }

  getWebconferenceRecordURL(params) {
    const stringified = params.isDownload
      ? queryString.stringify({ isDownload: params.isDownload }, { skipNull: true, skipEmptyString: true })
      : '';
    return this.client.get(
      `/content/academics-main/${params.academicMainId}/topics/${params.topicId}/webconference/records/${params.recordId
      }/url${stringified && `?${stringified}`}`
    );
  }

  sendAnswer(params) {
    return this.client.post(
      `/content/academics-main/${params.academicMainId}/topics/${params.topicId}/tasks`,
      params.body
    );
  }

  getGradeBookDetails(params) {
    return this.client.get(`/content/academics-main/${params.academicMainId}/grade-books/${params.gradeBookId}`);
  }

  persistGradeComposition(params) {
    return this.client.put(`/content/academics-main/${params.academicMainId}/grade-books/${params.gradeBookId}`, {
      hasGrade : true,
      grade    : params.composition
    });
  }

  getReferences(params) {
    return this.client.get(`/content/academics-main/${params.academicMainId}/topics/${params.topicId}/references`);
  }

  getQuestionByAcademicAndTopicId(params) {
    return this.client.get(
      `/content/academics-main/${params.academicMainId}/topics/${params.topicId}/questions/${params.questionId}`
    );
  }

  sendAnswerAvaliaActivity(params) {
    return this.client.post(
      `/content/academics-main/${params.academicMainId}/topics/${params.topicId}/avalia-activities`,
      {
        attemptNumber: params.studentCurrentAttempt
      }
    );
  }

  getAvaliaActivities(params) {
    const paramsFiltered = Object.entries(params).filter(([key, value]) => key !== 'academicMainId');
    const stringified = queryString.stringify(Object.fromEntries(paramsFiltered), {
      skipNull        : true,
      skipEmptyString : true
    });

    return this.client.get(
      `/content/academics-main/${params.academicMainId}/avalia-activities${stringified && `?${stringified}`}`
    );
  }

  sendEvaluateContent(params) {
    return this.client.post(
      `/content/academics-main/${params.academicMainId}/topics/${params.topicId}/evaluate-contents`,
      params.data
    );
  }

  copyContent(params) {
    return this.client.post(`/content/academics-main/${params.academicMainId}/topics/copy`, params.data);
  }

  getLTIDefault() {
    return this.client.post(`/content/academics-main/lti/client`);
  }

  getContentOriginType({ isToListTopics = false, isToHideOwnContent = false } = {}) {
    return this.client
      .get(`/content/institutions-configuration/content-origin-type?isToListTopics=${isToListTopics}&isToHideOwnContent=${isToHideOwnContent}`);
  }

  importMarketplaceContentsList({ academicMainId, payload }) {
    return this.client.post(`/content/academics-main/${academicMainId}/topics/marketplace/import`, payload);
  }

  getCorrectionEnrollments({ academicMainId, topicId, params }) {
    const query = validateQueryParameters(params);
    return this.client.get(
      `/content/academics-main/${academicMainId}/topics/${topicId}/corrections/enrollments?${query}`
    );
  }

  getCorrectionGroups({ academicMainId, topicId, params }) {
    const query = validateQueryParameters(params);
    return this.client.get(`/content/academics-main/${academicMainId}/topics/${topicId}/corrections/groups?${query}`);
  }

  getGroupAnswers({ academicMainId, topicId, groupId }) {
    return this.client.get(`/content/academics-main/${academicMainId}/topics/${topicId}/corrections/groups/${groupId}`);
  }

  getCorrectionCount({ academicMainId, topicId }) {
    return this.client.get(`/content/academics-main/${academicMainId}/topics/${topicId}/corrections/counts`);
  }

  getModuleDefaultGradeBooks({ search, contentOriginId, order, sortBy, page, perPage }) {
    const queryParams = validateQueryParameters({ search, contentOriginId, order, sortBy, page, perPage });
    return this.client.get(`/content/module-default-grade-books?${queryParams}`);
  }

  createModuleDefaultGradeBooks(body) {
    return this.client.post(`/content/module-default-grade-books`, body);
  }

  updateModuleDefaultGradeBooks({ id, body }) {
    return this.client.put(`/content/module-default-grade-books/${id}`, body);
  }

  deleteModuleDefaultGradeBooks(id) {
    return this.client.delete(`/content/module-default-grade-books/${id}`);
  }

  getScormLaunchLink({ academicMainId, topicId, scormId }) {
    return this.client.get(`/content/academic-main/${academicMainId}/topics/${topicId}/scorm/${scormId}/launch-link`);
  }

  getTrackerInfo({ academicMainId, topicType, topicId }) {
    return this.client.get(`/content/academics-main/${academicMainId}/contents/${topicType}/${topicId}`, { passOnMobileHeaders: true });
  }

  getEulaByProviderAlias({ providerAlias }) {
    return this.client.get(`/content/similarity/eula/${providerAlias}`);
  }

  acceptEula(params) {
    return this.client.post(`/content/similarity/eula/${params.providerAlias}/accept`, params.version ? { version: params.version } : {});
  }

  getSimilarityReportUrl({ providerAlias, enrollmentTaskId }) {
    return this.client.get(`/content/similarity/report/${providerAlias}/${enrollmentTaskId}`);
  }

  downloadAttachmentsRequest({ academicMainId, topicId }) {
    return this.client.post(`/content/academics-main/${academicMainId}/topics/${topicId}/enrollment-task-attachments`);
  }

  validateAvaliaActivityAccess({ accessCode = null, destinationPage, academicMainId, topicId }) {
    return this.client.post(`content/academics-main/${academicMainId}/topics/${topicId}/avalia-activities/sso${accessCode ? `?accessCode=${accessCode}` : ''}`, { destinationPage });
  }

  getSyncSummary({ academicMainId }) {
    return this.client.get(`/content/academics-main/${academicMainId}/contents/sync/preview`);
  }

  getAssociatedTopicProgress({ academicMainId, topicId }) {
    return this.client.get(`/content/academics-main/${academicMainId}/topics/${topicId}/deletion-impact`);
  }

  getContentToImport(academicMainId, originAcademicSyncTypeId) {
    const queryParams = validateQueryParameters({ originAcademicSyncTypeId });

    return this.client.get(`/content/academics-main/${academicMainId}/available-contents?${queryParams}`);
  }

  sendContentToAI({ academicMainId }) {
    return this.client.post(`/content/similarity/content-smart-integration`, { academicMainId });
  }
};

export default new ContentServiceV2();
